import { ReportGoalType } from "@api/graphql/types-and-hooks";

const UNCATEGORIZED_KEY = "__uncategorized__";

interface ReportDomainRef {
  id: string;
}

/**
 * Bucket goals by domain ID, sorted by rankOrder within each bucket.
 * Returns a Map keyed by domain ID (or "__uncategorized__" for goals without a domain).
 */
function bucketGoalsByDomain(goalList: ReportGoalType[]): {
  goalsByDomain: Map<string, ReportGoalType[]>;
} {
  const goalsByDomain = new Map<string, ReportGoalType[]>();
  // Sort by rankOrder, then break ties on createdDate (oldest first). Without
  // the createdDate tiebreaker, custom goals (no goal-bank link, rankOrder=0)
  // tie at 0 → the sort is a no-op and the order falls back to whatever the
  // BE returned. Since the BE response order can shift on any update (e.g.,
  // last-updated-date ordering after a revoke), goals would "jump" position
  // every time their summaryReviewed flipped. createdDate is immutable, so
  // bucket order stays stable across refetches.
  const sortedGoals = [...goalList].sort((a, b) => {
    const rankDelta = (a.goal?.rankOrder ?? 0) - (b.goal?.rankOrder ?? 0);
    if (rankDelta !== 0) return rankDelta;
    const aCreated = a.createdDate ? new Date(a.createdDate as string).getTime() : 0;
    const bCreated = b.createdDate ? new Date(b.createdDate as string).getTime() : 0;
    return aCreated - bCreated;
  });
  for (const goal of sortedGoals) {
    const domainId = goal.reportDomain?.id ?? UNCATEGORIZED_KEY;
    if (!goalsByDomain.has(domainId)) goalsByDomain.set(domainId, []);
    goalsByDomain.get(domainId)!.push(goal);
  }
  return { goalsByDomain };
}

/**
 * Return goals ordered by domain group: uncategorized first, then each report domain
 * in order, then any orphaned goals whose domain ID isn't in reportDomains.
 * When reportDomains is empty (still loading), uncategorized goals come first and
 * all domain-having goals follow as orphans — matching the sidebar's grouping.
 */
export function getGoalsOrderedByDomain(
  goalList: ReportGoalType[],
  reportDomains: ReportDomainRef[],
): ReportGoalType[] {
  const { goalsByDomain } = bucketGoalsByDomain(goalList);

  const orderedGoals: ReportGoalType[] = [];
  const uncategorized = goalsByDomain.get(UNCATEGORIZED_KEY) ?? [];
  orderedGoals.push(...uncategorized);

  for (const domain of reportDomains) {
    orderedGoals.push(...(goalsByDomain.get(domain.id) ?? []));
  }

  // Include goals whose domain ID is not in reportDomains (orphaned/stale references)
  const consumedDomainIds = new Set([UNCATEGORIZED_KEY, ...reportDomains.map((d) => d.id)]);
  for (const [domainId, goals] of goalsByDomain) {
    if (!consumedDomainIds.has(domainId)) {
      orderedGoals.push(...goals);
    }
  }

  return orderedGoals;
}

/** Minimal domain shape the sidebar/review render (id + name, plus the optional description DomainCard reads). */
export interface GroupedDomainRef {
  id: string;
  name: string;
  description?: string | null;
}

export interface DomainGoalGroup<TDomain extends GroupedDomainRef> {
  /** Domain id, or the uncategorized/orphan bucket key. */
  domainId: string;
  /** Display name: "Uncategorized" / the domain name / "Unknown Domain". */
  domainName: string;
  /** The report domain, or null for the uncategorized/orphan buckets. */
  domain: TDomain | null;
  goals: ReportGoalType[];
}

/**
 * Group goals by domain in display order — uncategorized first, then each report
 * domain in order (included even when it has no goals), then any orphaned domain
 * refs whose id isn't in `reportDomains`. Shared by the reassessment editing
 * sidebar and the reassessment review page so both group (and therefore number)
 * goals identically. Callers that only want non-empty groups filter on
 * `goals.length`.
 */
export function getGoalsGroupedByDomain<TDomain extends GroupedDomainRef>(
  goalList: ReportGoalType[],
  reportDomains: readonly TDomain[],
): DomainGoalGroup<TDomain>[] {
  const { goalsByDomain } = bucketGoalsByDomain(goalList);
  const groups: DomainGoalGroup<TDomain>[] = [];

  const uncategorized = goalsByDomain.get(UNCATEGORIZED_KEY) ?? [];
  if (uncategorized.length > 0) {
    groups.push({ domainId: UNCATEGORIZED_KEY, domainName: "Uncategorized", domain: null, goals: uncategorized });
  }

  const knownDomainIds = new Set<string>([UNCATEGORIZED_KEY, ...reportDomains.map((d) => d.id)]);
  for (const domain of reportDomains) {
    groups.push({
      domainId: domain.id,
      domainName: domain.name,
      domain,
      goals: goalsByDomain.get(domain.id) ?? [],
    });
  }

  // Goals whose domain id isn't in reportDomains (orphaned/stale references).
  for (const [domainId, goals] of goalsByDomain) {
    if (!knownDomainIds.has(domainId) && goals.length > 0) {
      groups.push({ domainId, domainName: "Unknown Domain", domain: null, goals });
    }
  }

  return groups;
}

export { UNCATEGORIZED_KEY, bucketGoalsByDomain };
export type { ReportDomainRef };
